import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  status: string;
  created: string;
  updated: string;
  entity_type: string;
  company_name: string;
  red_rock_full_entity_name: string;
  primary_phone: string;
  invoice_email: string;
}

export interface ClientFolderStatus {
  id: string;
  client_id: string;
  red_rock_id: string;
  status: boolean;
  folder_name?: string;
  client_folders?: string;
  created: string;
  updated: string;
}

export interface TaxReturnStatus {
  id: string;
  client_id: string;
  red_rock_id: string;
  card_status: boolean;
  text?: string;
  client_viewed?: boolean;
  created: string;
  updated: string;
}

class CustomerService {
  async getCustomer(customerId: string, userRole: string = 'client', userId: string = ''): Promise<Customer> {
    try {
      const response = await api.get(`/client-customer-view-access?id=${customerId}&role=${userRole}&userId=${userId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 403) {
        throw new Error('Access denied - you don\'t have permission to view this customer');
      }
      throw new Error('Failed to fetch customer data');
    }
  }

  async updateCustomer(customerId: string, data: Partial<Customer>): Promise<Customer> {
    try {
      const response = await api.patch(`/collections/customer_v2/records/${customerId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error('Failed to update customer data');
    }
  }

  async getClientFolderStatus(customerId: string, userRole: string = 'client', userId: string = ''): Promise<ClientFolderStatus | null> {
    try {
      const response = await api.get(`/collections/client_folder_status/records?filter=(red_rock_id='${customerId}')&expand=client_id,red_rock_id`);
      const data = response.data;
      
      if (data.items && data.items.length > 0) {
        return data.items[0];
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching client folder status:', error);
      return null;
    }
  }

  async getTaxReturnStatus(customerId: string, userRole: string = 'client', userId: string = ''): Promise<TaxReturnStatus | null> {
    try {
      const response = await api.get(`/collections/tax_return_status/records?filter=(red_rock_id='${customerId}')&expand=client_id,red_rock_id`);
      const data = response.data;
      
      if (data.items && data.items.length > 0) {
        return data.items[0];
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching tax return status:', error);
      return null;
    }
  }

  async updateTaxReturnStatusViewed(statusId: string): Promise<void> {
    try {
      await api.patch(`/collections/tax_return_status/records/${statusId}`, {
        client_viewed: true
      });
    } catch (error: any) {
      console.error('Error updating tax return status viewed:', error);
      throw new Error('Failed to update tax return status');
    }
  }
}

export const customerService = new CustomerService();
